import type { ComponentType, SVGProps } from 'react';
import {
  IconDashboard,
  IconUsers,
  IconCar,
  IconTrips,
  IconWallet,
  IconReceipt,
  IconFlag,
  IconChat,
  IconAward,
  IconBan,
  IconShield,
  IconLogs,
  IconMap,
  IconAlarm,
} from '@/components/ui/icons';
import type { AdminPermission } from '@/lib/adminAuth';

export interface AdminNavRoute {
  to: string;
  label: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  /** The permission a staffer needs to see this nav item. Every role holds the
   *  reads today, so all items show for any staffer; the gate is explicit so a
   *  future role with a narrower read set hides the right entries. */
  permission: AdminPermission;
}

/** Single source of truth for the Admin sidebar + router. Mirrors the core
 *  Filament resources staff manage day-to-day. Paths are at the ROOT — this is
 *  a dedicated admin portal, not a sub-app under '/admin'.
 *
 *  Dashboard is FIRST and is the default landing route (see AdminApp). Its
 *  `dashboard.read` permission is held by every role, so the landing always
 *  renders. */
export const ADMIN_NAV_ROUTES: AdminNavRoute[] = [
  { to: '/dashboard', label: 'Dashboard', icon: IconDashboard, permission: 'dashboard.read' },
  { to: '/customers', label: 'Customers', icon: IconUsers, permission: 'customers.read' },
  { to: '/customers/identity', label: 'Customer Identity', icon: IconUsers, permission: 'customer_identity.read' },
  { to: '/drivers', label: 'Drivers', icon: IconCar, permission: 'drivers.read' },
  { to: '/trips', label: 'Trips', icon: IconTrips, permission: 'trips.read' },
  { to: '/reserve-trips', label: 'Reserve Trips', icon: IconTrips, permission: 'reserve_trips.read' },
  { to: '/boat-trips', label: 'Boat bookings', icon: IconTrips, permission: 'boat_trips.read' },
  { to: '/wallet', label: 'Wallet', icon: IconWallet, permission: 'wallet.read' },
  { to: '/wallet-approvals', label: 'Wallet Approvals', icon: IconShield, permission: 'wallet.adjust' },
  { to: '/payments', label: 'Payments', icon: IconReceipt, permission: 'payments.read' },
  { to: '/driver-tips', label: 'Driver Tips', icon: IconReceipt, permission: 'driver_tips.read' },
  { to: '/driver-expenses', label: 'Driver Expenses', icon: IconReceipt, permission: 'expenses.read' },
  { to: '/payouts', label: 'Payouts', icon: IconWallet, permission: 'payouts.read' },
  { to: '/payout-kyc', label: 'Payout-KYC', icon: IconShield, permission: 'payouts.kyc_verify' },
  { to: '/payout-kyc-review', label: 'Bank-KYC Review', icon: IconShield, permission: 'bank_kyc_review.read' },
  { to: '/driver-earnings', label: 'Driver Earnings', icon: IconReceipt, permission: 'driver_earnings.read' },
  { to: '/fares', label: 'Fares', icon: IconReceipt, permission: 'fares.read' },
  { to: '/membership-tiers', label: 'Membership Tiers', icon: IconAward, permission: 'membership.read' },
  { to: '/destination-settings', label: 'Going Home', icon: IconReceipt, permission: 'destinations.read' },
  { to: '/operations', label: 'Operations', icon: IconReceipt, permission: 'features.read' },
  { to: '/complaints', label: 'Complaints', icon: IconFlag, permission: 'complaints.read' },
  { to: '/support', label: 'Support', icon: IconChat, permission: 'support.read' },
  { to: '/incentives', label: 'Incentives', icon: IconAward, permission: 'incentives.read' },
  { to: '/quests', label: 'Quests', icon: IconAward, permission: 'quests.read' },
  { to: '/quest-tiers', label: 'Quest Tiers', icon: IconAward, permission: 'quests.read' },
  { to: '/quest-optins', label: 'Quest Opt-ins', icon: IconAward, permission: 'quests.read' },
  { to: '/streaks', label: 'Streaks', icon: IconAward, permission: 'streaks.read' },
  { to: '/guarantee-enrollments', label: 'Guarantee Enrollments', icon: IconShield, permission: 'guarantees.read' },
  { to: '/boosts', label: 'Boosts', icon: IconAward, permission: 'boosts.read' },
  { to: '/promos', label: 'Promo codes', icon: IconReceipt, permission: 'promos.read' },
  { to: '/content', label: 'Content', icon: IconLogs, permission: 'cms.read' },
  { to: '/zones', label: 'Zones', icon: IconMap, permission: 'zones.read' },
  { to: '/trip-types', label: 'Trip types', icon: IconTrips, permission: 'trip_types.read' },
  { to: '/vehicle-categories', label: 'Vehicle Categories', icon: IconCar, permission: 'vehicle_categories.read' },
  { to: '/complaint-categories', label: 'Complaint Categories', icon: IconFlag, permission: 'complaint_categories.read' },
  { to: '/booking-statuses', label: 'Booking Statuses', icon: IconLogs, permission: 'booking_statuses.read' },
  { to: '/docks', label: 'Docks', icon: IconMap, permission: 'docks.read' },
  { to: '/chargers', label: 'Chargers', icon: IconMap, permission: 'chargers.read' },
  { to: '/notifications', label: 'Notifications', icon: IconAlarm, permission: 'notifications.read' },
  // Both read safety-svc data and admin-svc gates them on safety.read — the
  // old audit.read mapping here showed the entries to auditors who then got a
  // 403, and hid them from the safety team who could actually use them.
  { to: '/safety', label: 'Trust & Safety', icon: IconBan, permission: 'safety.read' },
  { to: '/speeding', label: 'Speeding', icon: IconBan, permission: 'speeding.read' },
  { to: '/staff', label: 'Staff', icon: IconShield, permission: 'staff.manage' },
  { to: '/roles', label: 'Roles', icon: IconShield, permission: 'staff.manage' },
  { to: '/audit', label: 'Audit Log', icon: IconLogs, permission: 'audit.read' },
  { to: '/otp-codes', label: 'OTP Codes', icon: IconShield, permission: 'otp_codes.read' },
  { to: '/push-log', label: 'Push Log', icon: IconAlarm, permission: 'push_log.read' },
  { to: '/verification-log', label: 'ID Verification', icon: IconShield, permission: 'verification_log.read' },
];

/** The nav entries this permission set can actually reach, in nav order. */
export function accessibleRoutes(perms: readonly string[]): AdminNavRoute[] {
  return ADMIN_NAV_ROUTES.filter((r) => perms.includes(r.permission));
}

/** Where to send a staffer who lands on '/' or on a page they can't open.
 *
 *  Dashboard used to be hardcoded as the landing route, so a role WITHOUT
 *  dashboard.read was dropped onto a page it couldn't read; the resulting 403
 *  logged them out (see lib/api.ts). The landing is now simply the first nav
 *  entry the role can reach — Dashboard for most people, Support for a
 *  support-only role, and so on. null ⇒ the role can reach nothing at all,
 *  which the shell reports rather than looping. */
export function landingPath(perms: readonly string[]): string | null {
  return accessibleRoutes(perms)[0]?.to ?? null;
}

/** The permission a path requires, or null when the path isn't permission-
 *  gated. Sub-paths inherit their parent's permission (e.g. '/trips/42'). */
export function permissionForPath(path: string): AdminPermission | null {
  const exact = ADMIN_NAV_ROUTES.find((r) => r.to === path);
  if (exact) return exact.permission;
  // Longest matching prefix wins, so '/customers/identity' beats '/customers'.
  const prefixed = ADMIN_NAV_ROUTES.filter((r) => path.startsWith(r.to + '/')).sort(
    (a, b) => b.to.length - a.to.length,
  );
  return prefixed[0]?.permission ?? null;
}

/** The sidebar item(s) a permission unlocks, by label. The role builder shows
 *  these next to each permission and searches them, so an operator can find a
 *  permission by the MENU NAME they know ("Operations") rather than having to
 *  guess the internal wording ("feature settings"). Empty for permissions that
 *  gate an action rather than a page (e.g. wallet.adjust). */
export function pagesForPermission(permission: string): string[] {
  return ADMIN_NAV_ROUTES.filter((r) => r.permission === permission).map((r) => r.label);
}

/** A labelled sidebar category — a small UPPERCASE header over a set of items,
 *  collapsible in the shell. `paths` reference entries in ADMIN_NAV_ROUTES by
 *  their `to`, so the router/permission list stays the ONE source of truth and
 *  a section can never drift from it. Order here is the display order. */
export interface AdminNavGroup {
  /** The small uppercase muted header shown above the group. */
  label: string;
  /** Routes in this group, by their `to` path (in display order). */
  paths: string[];
}

/** The Laravel-style grouped sidebar structure. Buckets the flat routes above
 *  into the product's functional areas. Any route NOT listed in a group falls
 *  into a trailing "More" group in the shell, so a newly-added nav entry can
 *  never silently disappear. */
export const ADMIN_NAV_GROUPS: AdminNavGroup[] = [
  { label: 'Overview', paths: ['/dashboard'] },
  { label: 'Operations', paths: ['/trips', '/reserve-trips', '/boat-trips'] },
  { label: 'People', paths: ['/customers', '/customers/identity', '/drivers'] },
  { label: 'Finance', paths: ['/wallet', '/wallet-approvals', '/payments', '/driver-tips', '/driver-expenses', '/payouts', '/payout-kyc', '/payout-kyc-review', '/driver-earnings', '/fares', '/membership-tiers'] },
  { label: 'Engagement', paths: ['/incentives', '/quests', '/quest-tiers', '/quest-optins', '/streaks', '/guarantee-enrollments', '/boosts', '/promos', '/notifications'] },
  { label: 'Support & Safety', paths: ['/complaints', '/support', '/safety', '/speeding'] },
  { label: 'Content & Config', paths: ['/content', '/zones', '/trip-types', '/vehicle-categories', '/complaint-categories', '/booking-statuses', '/docks', '/chargers'] },
  { label: 'Administration', paths: ['/staff', '/roles', '/audit', '/otp-codes', '/push-log'] },
];

/** A resolved sidebar group: its header label + the route objects to render. */
export interface ResolvedNavGroup {
  label: string;
  items: AdminNavRoute[];
}

/** Resolve ADMIN_NAV_GROUPS against a (permission-filtered) route list into
 *  render-ready groups. Empty groups are dropped; any route the groups didn't
 *  cover lands in a trailing "More" group so nothing is ever lost. */
export function resolveNavGroups(routes: AdminNavRoute[]): ResolvedNavGroup[] {
  const byPath = new Map(routes.map((r) => [r.to, r]));
  const used = new Set<string>();

  const groups: ResolvedNavGroup[] = ADMIN_NAV_GROUPS.map((g) => {
    const items = g.paths
      .map((p) => byPath.get(p))
      .filter((r): r is AdminNavRoute => Boolean(r));
    items.forEach((r) => used.add(r.to));
    return { label: g.label, items };
  }).filter((g) => g.items.length > 0);

  const leftover = routes.filter((r) => !used.has(r.to));
  if (leftover.length) groups.push({ label: 'More', items: leftover });

  return groups;
}
